/**
 * Reading tasks from the core doc (spec §8.1). Pure functions over a LoroDoc; ops.ts writes.
 * `nodeId` ↔ tree meta `taskId` per ADR 0003.
 */
import type { LoroDoc, LoroMap, LoroText, LoroTree } from "loro-crdt";
import { C, type PropVal, type Task } from "./schema";

export type TaskMap = LoroMap<{
  id: string;
  typeId: string;
  title: LoroText;
  notes: LoroText;
  createdAt: string;
  doneAt: string | null;
  archivedAt: string | null;
  doneBy?: string;
  orderedChildren: boolean;
  nodeId?: string;
  props: LoroMap<Record<string, PropVal>>;
  attachments: LoroMap<Record<string, unknown>>;
}>;

export function tasksMap(doc: LoroDoc): LoroMap<Record<string, TaskMap>> {
  return doc.getMap(C.tasks) as unknown as LoroMap<Record<string, TaskMap>>;
}

export function taskMap(doc: LoroDoc, id: string): TaskMap | undefined {
  const m = tasksMap(doc).get(id);
  return m && typeof m === "object" && "kind" in m ? (m as unknown as TaskMap) : undefined;
}

export function readTask(doc: LoroDoc, id: string): Task | undefined {
  const m = taskMap(doc, id);
  if (!m) return undefined;
  return fromJson(m.toJSON() as Record<string, unknown>, id);
}

function fromJson(j: Record<string, unknown>, id: string): Task {
  return {
    id,
    typeId: String(j.typeId ?? "task"),
    title: String(j.title ?? ""),
    notes: String(j.notes ?? ""),
    createdAt: String(j.createdAt ?? ""),
    doneAt: (j.doneAt as string | null) ?? null,
    archivedAt: (j.archivedAt as string | null) ?? null,
    doneBy: j.doneBy as string | undefined,
    orderedChildren: Boolean(j.orderedChildren),
    nodeId: j.nodeId as string | undefined,
    props: (j.props as Record<string, PropVal>) ?? {},
    attachments: (j.attachments as Task["attachments"]) ?? {},
  };
}

/** Every task, as plain objects. O(n); widgets filter and sort from here. */
export function listTasks(doc: LoroDoc): Task[] {
  const all = tasksMap(doc).toJSON() as Record<string, unknown>;
  const out: Task[] = [];
  for (const [id, j] of Object.entries(all)) {
    if (j && typeof j === "object" && !Array.isArray(j)) out.push(fromJson(j as Record<string, unknown>, id));
  }
  return out;
}

export function tree(doc: LoroDoc): LoroTree {
  return doc.getTree(C.tree);
}

/** parent task id via the tree (ADR 0003), or null for a root-level task. */
export function parentOf(doc: LoroDoc, task: Pick<Task, "nodeId">): string | null {
  if (!task.nodeId) return null;
  const node = tree(doc).getNodeByID(task.nodeId as `${number}@${number}`);
  const parent = node?.parent();
  const tid = parent?.data.get("taskId");
  return typeof tid === "string" ? tid : null;
}

/** child task ids in sibling order. */
export function childrenOf(doc: LoroDoc, task: Pick<Task, "nodeId">): string[] {
  if (!task.nodeId) return [];
  const node = tree(doc).getNodeByID(task.nodeId as `${number}@${number}`);
  const kids = node?.children() ?? [];
  return kids.map((k) => k.data.get("taskId")).filter((v): v is string => typeof v === "string");
}
